/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useRef} from "react";
import {useTranslation} from "react-i18next";
import useSellerTimeslots from "utils/hooks/useSellerTimeslots";
import {withRouter} from "react-router";
import {RouteComponentProps} from "react-router-dom";
import {CalendarDaysIcon} from "@heroicons/react/24/solid";
import {ClockIcon, CubeTransparentIcon, DeviceTabletIcon, UsersIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";
import BookingCardTitle from "pages/Front/Booking/components/BookingCardTitle";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import TableHead from "pages/Front/components/Table/TableHead";
import TableHeadColumn from "pages/Front/components/Table/TableHeadColumn";
import Table from "pages/Front/components/Table/Table";
import TableBody from "pages/Front/components/Table/TableBody";
import TableRow from "pages/Front/components/Table/TableRow";
import TableColumnResponsive from "pages/Front/components/Table/TableColumnResponsive";
import Dates from "utils/Dates";
import {getShortNameFromId} from "utils/reservationUtils";
import TableHeadTitle from "pages/Front/components/Table/TableHeadTitle";

interface ReservationForDateParams {}

interface ReservationForDateProps extends RouteComponentProps<ReservationForDateParams> {
    className?: string;
}

const ReservationsForDate = (props: ReservationForDateProps) => {
    const {t} = useTranslation();
    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);

    const {reservationsForDate} = useSellerTimeslots(
        () => {},
        props.history,
        [scrollRefTimeslots, scrollRefCustomTimeslots, scrollRefConfirmTimeslots],
        () => {},
        1
    );

    return (
        <BookingCard className={clsx("ReservationsForDate !px-0 !py-0", props.className)}>
            <BookingCardTitle className={"px-3 py-2.5 xl:px-3 xl:py-3"} title={"Other reservations for this date"} />
            <div className={"flex-col w-full flex justify-end"}>
                <Table responsive={true}>
                    <TableHead className={"border-t"}>
                        <TableHeadColumn>
                            <TableHeadTitle className={"justify-start"}>
                                <CalendarDaysIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                                <span className={"flex shrink-0"}>{t("Date")}</span>
                            </TableHeadTitle>
                        </TableHeadColumn>
                        <TableHeadColumn>
                            <TableHeadTitle className={"justify-center"}>
                                <ClockIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                                <span className={"flex shrink-0"}>{t("Time")}</span>
                            </TableHeadTitle>
                        </TableHeadColumn>
                        <TableHeadColumn>
                            <TableHeadTitle className={"justify-center"}>
                                <UsersIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                                <span className={"flex shrink-0"}>{t("Persons")}</span>
                            </TableHeadTitle>
                        </TableHeadColumn>
                        <TableHeadColumn>
                            <TableHeadTitle className={"justify-start"}>
                                <CubeTransparentIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                                <span className={"flex shrink-0"}>{t("Package")}</span>
                            </TableHeadTitle>
                        </TableHeadColumn>
                        <TableHeadColumn>
                            <TableHeadTitle className={"justify-start"}>
                                <DeviceTabletIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                                <span className={"flex shrink-0"}>{t("Origin")}</span>
                            </TableHeadTitle>
                        </TableHeadColumn>
                    </TableHead>
                    <TableBody>
                        {reservationsForDate &&
                            reservationsForDate.length > 0 &&
                            reservationsForDate.map((reservation, index) => {
                                return (
                                    <TableRow key={index}>
                                        <TableColumnResponsive>{Dates.date(reservation.date)}</TableColumnResponsive>
                                        <TableColumnResponsive classNameValue={"justify-center"}>
                                            {reservation.hour}:{reservation.minute == 0 ? "00" : reservation.minute}
                                        </TableColumnResponsive>
                                        <TableColumnResponsive classNameValue={"justify-center"}>{reservation.persons}</TableColumnResponsive>
                                        <TableColumnResponsive>{getShortNameFromId(reservation.category ?? "---")}</TableColumnResponsive>
                                        <TableColumnResponsive>{reservation.origin}</TableColumnResponsive>
                                    </TableRow>
                                );
                            })}
                        {reservationsForDate && reservationsForDate.length === 0 && (
                            <TableRow>
                                <td className={"py-3 px-1.5 first:!pl-3 last:!pr-3 align-middle truncate text-center"} colSpan={5}>{t("No other reservations for this date")}</td>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </BookingCard>
    );
};

export default withRouter(ReservationsForDate);
